import type { KingsCrownViewerState } from "@cloudnest/kings-crown-engine";
import { useSyncExternalStore } from "react";
import { createGameApi } from "../../shared/services/game-api";
import { getCurrentUserId } from "../../shared/store/auth-store";

const gameApi = createGameApi("kings-crown", "game-platform");

export interface KingsCrownMultiplayerState {
  readonly viewerState: KingsCrownViewerState | null;
  readonly lastError: { readonly error: string } | null;
  readonly connection:
    | "idle"
    | "connecting"
    | "open"
    | "reconnecting"
    | "closed";
  readonly lastAppliedSeq: number;
  readonly roomId: string | null;
  readonly myPlayerId: string | null;
  readonly isHost: boolean;
  readonly isObserver: boolean;
}

interface ViewerStateRow {
  readonly sequenceNumber: number;
  readonly state: Record<string, unknown>;
}

const INITIAL_STATE: KingsCrownMultiplayerState = {
  viewerState: null,
  lastError: null,
  connection: "idle",
  lastAppliedSeq: 0,
  roomId: null,
  myPlayerId: null,
  isHost: false,
  isObserver: false,
};

let state: KingsCrownMultiplayerState = { ...INITIAL_STATE };
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

function createActionId(prefix: string): string {
  if ("randomUUID" in crypto) {
    return `${prefix}:${crypto.randomUUID()}`;
  }
  return `${prefix}:${Date.now()}:${Math.random().toString(16).slice(2)}`;
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    globalThis.setTimeout(resolve, ms);
  });
}

function adoptLatest(rows: readonly ViewerStateRow[], fromSeq: number): number {
  let bestSeq = fromSeq;
  let best: Record<string, unknown> | null = null;
  for (const row of rows) {
    if (row.sequenceNumber > bestSeq) {
      bestSeq = row.sequenceNumber;
      best = row.state;
    }
  }

  if (best !== null) {
    state = {
      ...state,
      viewerState: best as unknown as KingsCrownViewerState,
      lastAppliedSeq: bestSeq,
    };
  }

  return bestSeq;
}

function setError(error: unknown, fallback: string): void {
  state = {
    ...state,
    lastError: {
      error: error instanceof Error ? error.message : fallback,
    },
  };
  notify();
}

async function fetchProjected(roomId: string, fromSeq = 0): Promise<void> {
  const projected = await gameApi.getViewerStates(roomId, fromSeq);
  if (projected.success && projected.data && state.roomId === roomId) {
    adoptLatest(projected.data, fromSeq);
  }
}

async function adoptProjectedViewerState(
  roomId: string,
  fromSeq = 0,
): Promise<boolean> {
  for (let attempt = 0; attempt < 10; attempt += 1) {
    const projected = await gameApi.getViewerStates(roomId, fromSeq);
    if (!projected.success || !projected.data || state.roomId !== roomId) {
      return false;
    }

    if (projected.data.some((row) => row.sequenceNumber > fromSeq)) {
      adoptLatest(projected.data, fromSeq);
      return true;
    }

    await wait(160 + attempt * 80);
  }

  return false;
}

async function submitAndRefresh(
  eventType: string,
  payload: Record<string, unknown>,
): Promise<{ readonly success: boolean; readonly error?: string }> {
  const roomId = state.roomId;
  if (!roomId) return { success: false, error: "ERR_NO_GAME" };
  if (state.isObserver && eventType !== "SyncViewerState") {
    return { success: false, error: "ERR_OBSERVER_READ_ONLY" };
  }

  try {
    const res = await gameApi.submitEvent(roomId, eventType, payload);
    if (!res.success) {
      const error = res.error ?? "ERR_KINGS_CROWN_ACTION";
      state = { ...state, lastError: { error } };
      notify();
      return { success: false, error };
    }
    await fetchProjected(roomId, state.lastAppliedSeq);
    if (state.roomId === roomId) {
      state = { ...state, lastError: null };
      notify();
    }
    return { success: true };
  } catch (error) {
    setError(error, "ERR_KINGS_CROWN_ACTION");
    return { success: false, error: "ERR_KINGS_CROWN_ACTION" };
  }
}

export const kingsCrownMultiplayerActions = {
  initializeAsHost: async (
    roomId: string,
    playerIds: readonly string[],
    firstPlayerId?: string,
  ): Promise<void> => {
    const myPlayerId = getCurrentUserId() ?? "";
    state = {
      ...INITIAL_STATE,
      roomId,
      myPlayerId,
      isHost: true,
      isObserver: false,
      connection: "connecting",
    };
    notify();

    try {
      const existing = await gameApi.getViewerStates(roomId);
      const existingRows =
        existing.success && existing.data ? existing.data : [];
      if (existingRows.length > 0) {
        adoptLatest(existingRows, 0);
        state = { ...state, connection: "open" };
        notify();
        return;
      }

      const init = await gameApi.submitEvent(roomId, "init", {
        playerIds,
        firstPlayerId,
      });
      if (!init.success) {
        throw new Error(init.error ?? "ERR_KINGS_CROWN_INIT_REJECTED");
      }
      await adoptProjectedViewerState(roomId, 0);
      state = { ...state, connection: "open" };
      notify();
    } catch (error) {
      state = { ...state, connection: "closed" };
      setError(error, "ERR_KINGS_CROWN_INIT_FAILED");
    }
  },

  initializeAsViewer: async (
    roomId: string,
    isObserver: boolean,
  ): Promise<void> => {
    const myPlayerId = getCurrentUserId() ?? "";
    state = {
      ...INITIAL_STATE,
      roomId,
      myPlayerId,
      isHost: false,
      isObserver,
      connection: "connecting",
    };
    notify();

    try {
      const adopted = await adoptProjectedViewerState(roomId, 0);
      if (!adopted && state.roomId === roomId) {
        const sync = await gameApi.submitEvent(roomId, "SyncViewerState", {
          actionId: createActionId("sync-viewer"),
        });
        if (sync.success) {
          await adoptProjectedViewerState(roomId, state.lastAppliedSeq);
        } else {
          await adoptProjectedViewerState(roomId, 0);
        }
      }
      state = { ...state, connection: "open" };
      notify();
    } catch (error) {
      state = { ...state, connection: "closed" };
      setError(error, "ERR_KINGS_CROWN_FETCH_FAILED");
    }
  },

  replayEvents: async (roomId: string): Promise<void> => {
    try {
      await fetchProjected(roomId, state.lastAppliedSeq);
      notify();
    } catch (error) {
      setError(error, "ERR_KINGS_CROWN_REPLAY_FAILED");
    }
  },

  applyRemoteEvent: (
    payloadState: Record<string, unknown>,
    seq: number,
  ): void => {
    if (seq <= state.lastAppliedSeq) return;
    state = {
      ...state,
      viewerState: payloadState as unknown as KingsCrownViewerState,
      lastAppliedSeq: seq,
    };
    notify();
  },

  drawDraftOptions: () =>
    submitAndRefresh("DrawDraftOptions", {
      actionId: createActionId("draw"),
    }),

  pickDraftToken: (source: "bag" | "table", slotId?: string) =>
    submitAndRefresh("PickDraftToken", {
      actionId: createActionId("pick"),
      source,
      ...(slotId ? { slotId } : {}),
    }),

  confirmPlacement: (chipId: string, cellId: string) =>
    submitAndRefresh("ConfirmPlacement", {
      actionId: createActionId("place"),
      chipId,
      cellId,
    }),

  reset: (): void => {
    state = { ...INITIAL_STATE };
    notify();
  },
};

export function useKingsCrownMultiplayerStore(): KingsCrownMultiplayerState {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => state,
  );
}
